/* eslint-disable no-useless-escape */

import { DataDogLogger } from 'utils/dataDog';
import { parse } from 'utils/html';
import { getIsNativeMobileApp } from 'utils/mobile';

export const getUrlParams = (param: string) => {
  const { search } = window.location;
  const params = new URLSearchParams(search);
  return params.get(param);
};

/**
 * NOTE: the only subdomain we allow is "www".
 * NOTE: This regex is global, so be careful with regex state.
 */
export const urlRegExp =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&;=\/]*)/gim;

export const urlAtTheEndRegExp = new RegExp(urlRegExp.source + /(\s\.)?$/.source, 'gim');

export const hostnameRegExp = /:\/\/(www[0-9]?\.)?(?<hostname>[\w\.]*)/i;

/**
 * Check if a string contains a URL.
 * @param s
 * @returns
 */
export const containsUrl = (s?: string | null) => {
  if (!s) return { result: false, endsWith: false };

  const contains = urlRegExp.test(s);
  const endsWith = urlAtTheEndRegExp.test(s);

  const result = { result: contains, endsWith };

  // Reset Regex state
  urlRegExp.lastIndex = 0;
  urlAtTheEndRegExp.lastIndex = 0;

  return result;
};

/**
 * Given a string, find the url.
 * @param s a string possibly containing a url.
 * @returns Matched URL. If none are found, return null. If multiple URLs are found, return the last URL.
 */
export const getUrl = (s: string) => {
  const result = s.match(urlRegExp);

  if (!result || result.length === 0) return null;
  const url = parse(result.pop() || '');
  return url;
};

/**
 * Given a string, find the URLs.
 * @param s a string possibly containing multiple URLs.
 * @returns Matched URL. If none are found, return null.
 */
export const getUrls = (s: string) => {
  const result = s.match(urlRegExp);

  if (!result || result.length === 0) return null;

  return result.map((url) => parse(url));
};

/**
 * Find the hostname in a string and returned a pretty hostname.
 * @param s A string possibly containing a hostname.
 * @returns If no hostname is found, return null. Otherwise, return hostname.
 * If multiple hostnames are found, return the first one.
 */
export const displayHostname = (s: string) => {
  const match = s.match(hostnameRegExp);
  if (match != null && match.groups && match.groups?.hostname) {
    return match.groups.hostname;
  }
  return '';
};

/**
 * Check if the URL is a non-https protocol; if so, log an error.
 * @param url
 * @returns True if the URL is a https protocol; false otherwise.
 */
export const validateURL = (url: string) => {
  try {
    if (!url) return false;
    const parsed = new URL(url);
    return ['https:'].includes(parsed.protocol);
  } catch (err) {
    console.error(err);
  }

  return false;
};

/**
 * Given a URL, force the HTTPS protocol to be used.
 *
 * @param url String representing a URL
 * @returns HTTPS url; empty string if url could not be forced to use HTTPS
 */
export const forceHttps = (url: string) => {
  try {
    const parsed = new URL(url);
    parsed.protocol = 'https:';
    // Remove trailing slash to preserve original url
    if (!url.endsWith('/')) return parsed.toString().replace(/\/?$/, '');

    return parsed.toString();
  } catch (e: unknown) {
    DataDogLogger.error(new Error(`Could not force https protocol`), { url });
    return '';
  }
};

export const linkify = (inputText: string, url: string) => {
  if (!inputText) {
    DataDogLogger.error(
      new Error('Error creating linkify messages. inputText is empty or undefined'),
    );
    return '';
  }
  if (!url) {
    DataDogLogger.error(new Error('Error creating linkify messages. url is empty or undefined'));
  }

  validateURL(url);

  return inputText.replace(
    url,
    `<a href="${forceHttps(url)}" target="_blank">${displayHostname(url)}</a>`,
  );
};

/**
 * Give a `message` that has URL links in the text, convert these into anchor tags.
 * If the URL is at the end of the `message`, remove the URL since it will be displayed as a `UrlInformation` card.
 * @param message Text that may contain a URL
 * @returns
 */
export const linkifyMessage = (message: string) => {
  const url = getUrl(message);
  if (!url) {
    return message;
  }

  // Remove hyperlinks, if last thing in the message
  const messageWithoutLinks = message.replace(urlAtTheEndRegExp, '');

  // If message is empty after removing links, use default text
  if (messageWithoutLinks.length === 0) {
    validateURL(url);
    return `<a href="${forceHttps(url)}" target="_blank">Check it out</a>`;
  }

  // Linkify other links
  return linkify(messageWithoutLinks, url);
};

/**
 * Check if current location is local
 * @returns
 */
export const isLocalHost = () => {
  const hostname = window?.location?.hostname;
  const url = window?.location?.href;
  const hostnameFromUrl = displayHostname(url);
  const parts = hostnameFromUrl.split('.');

  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    parts.pop() === 'localhost' ||
    parts.pop() === 'localhost'
  );
};

/**
 * Converts all URLs in a `message` to be `https`
 * @param message A string the may contain multiple URLs
 * @returns an object with `message` with all the URLs converted to `https`. `invalidUrls` is a list of URLs that were updated to be safe.
 */
export const convertLinksToHttps = (message: string) => {
  const invalidUrls: string[] = [];

  if (!message) return { message, invalidUrls };

  const urls = getUrls(message);
  if (!urls) return { message, invalidUrls };

  for (const url of urls) {
    const safeUrl = forceHttps(url);
    if (url !== safeUrl) invalidUrls.push(url);
    message = message.replace(url, safeUrl);
  }

  return { message, invalidUrls };
};

/**
 * Wrapper for `window.open`.
 * Given a URL, open the link in a new tab
 * @param url The URL to open
 * @param target Where to open the URL
 * @param features Window features
 */
export const open = (url?: string, target = '_blank', features = 'noreferrer') => {
  if (!url) return;

  if (getIsNativeMobileApp()) {
    // iOS app doesn't parse links as expected. They are also expecting <a> tags in particular
    // Safari issue: https://stackoverflow.com/questions/20696041/window-openurl-blank-not-working-on-imac-safari
    const linkElem = document.createElement('a');
    const linkId = `iOS-link-${url}`;
    linkElem.id = linkId;
    window.document.body.appendChild(linkElem);
    const link = document.getElementById(linkId);
    link && link.setAttribute('href', url);
    link && link.click();
  } else {
    window.open(forceHttps(url), target, features);
  }
};
